import i18n from '@/libs/i18n'
import { PrintSuccess } from '@/libs/toast'

const UNITS = ['B', 'KB', 'MB', 'GB', 'TB']

export const formatBytes = (bytes?: number | null, digits = 1): string => {
    if (!bytes || bytes <= 0) return '0 B'

    let value = bytes
    let unit  = 0

    while (value >= 1024 && unit < UNITS.length - 1) {
        value /= 1024
        unit++
    }

    return `${value.toFixed(unit === 0 ? 0 : digits)} ${UNITS[unit]}`
}

// Both halves of a used/total pair in one unit — the total's — so `1.2 / 2.0 GB` reads as
// one measurement instead of as two numbers that happen to sit next to each other.
export const formatBytesPair = (used: number, total: number): string => {
    if (total <= 0) return formatBytes(used)

    let scale = 1
    let unit  = 0

    while (total / scale >= 1024 && unit < UNITS.length - 1) {
        scale *= 1024
        unit++
    }

    const digits = unit === 0 ? 0 : 1

    return `${(Math.max(used, 0) / scale).toFixed(digits)} / ${(total / scale).toFixed(digits)} ${UNITS[unit]}`
}

// A live transfer rate, in the same units a size is written in: `1.2 MB/s`. A rate the
// plane could not measure — the first sample of an environment, or one too long after the
// last — is a dash, never a zero: nothing moving and nothing known are different answers.
export const formatRate = (bytesPerSec?: number | null): string =>
    bytesPerSec === null || bytesPerSec === undefined ? '—' : `${formatBytes(bytesPerSec)}/s`

export const bytesToMB = (bytes?: number | null): number => Math.round((bytes ?? 0) / (1024 * 1024))

export const bytesToGB = (bytes?: number | null): number => Math.round((bytes ?? 0) / (1024 * 1024 * 1024))

export const mbToBytes = (mb: number): number => Math.round(mb * 1024 * 1024)

export const gbToBytes = (gb: number): number => Math.round(gb * 1024 * 1024 * 1024)

export const usedPercent = (total?: number | null, free?: number | null): number => {
    if (!total || total <= 0) return 0
    return Math.min(100, Math.max(0, Math.round(((total - (free ?? 0)) / total) * 100)))
}

export const copyToClipboard = async (value: string) => {
    await navigator.clipboard.writeText(value)
    PrintSuccess(i18n.global.t('general.copied'))
}

// The `?redirect=` of a gate — the login screen's and the preparing page's — read back as
// something safe to hand to the router. Only a path of this app is accepted: a protocol or
// a `//host` would leave the app entirely, which is not what a redirect inside it means.
export const safeRedirect = (value: unknown, fallback = '/home'): string => {
    const path = typeof value === 'string' ? value : ''

    return path.startsWith('/') && !path.startsWith('//') ? path : fallback
}

// Some plane list routes answer with a bare array and some with the house ListResponse
// envelope; both are accepted here so the views stay identical.
export const unwrapList = <T>(payload: unknown): { items: T[], pagination: import('@/types/Common').Pagination | null } => {
    if (Array.isArray(payload)) return { items: payload as T[], pagination: null }

    const envelope = payload as { items?: T[] | null, pagination?: import('@/types/Common').Pagination }
    return { items: envelope?.items ?? [], pagination: envelope?.pagination ?? null }
}
